import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  CreditCardIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import adminAPI from '../utils/api';
import toast from 'react-hot-toast';

const ServiceModal = ({ isOpen, onClose, service, mode, onSuccess }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    type: 'pack',
    description: '',
    total_sessions: '',
    monthly_sessions: '',
    price: '',
    duration_days: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [clientServices, setClientServices] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  useEffect(() => {
    if (service && (mode === 'edit' || mode === 'view')) {
      setFormData({
        name: service.name || '',
        type: service.type || 'pack',
        description: service.description || '',
        total_sessions: service.session_count || '',
        monthly_sessions: service.session_count || '',
        price: service.price || '',
        duration_days: service.duration_days || '',
        is_active: service.is_active ?? true
      });
      
      // Fetch clients using this service if viewing or editing
      if (mode === 'view' || mode === 'edit') {
        fetchServiceClients();
      }
    } else {
      setFormData({
        name: '',
        type: 'pack',
        description: '',
        total_sessions: '',
        monthly_sessions: '',
        price: '',
        duration_days: '',
        is_active: true
      });
      setClientServices([]);
    }
    setErrors({});
  }, [service, mode, isOpen]);

  const fetchServiceClients = async () => {
    if (!service?.id) return;
    
    try {
      setLoadingClients(true);
      const clients = await adminAPI.getServiceClients(service.id);
      setClientServices(Array.isArray(clients) ? clients : (clients.results || []));
    } catch (error) {
      console.error('Error fetching service clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const serviceData = {
        name: formData.name,
        type: formData.type,
        description: formData.description,
        price: parseFloat(formData.price) || 0,
        duration_days: parseInt(formData.duration_days) || null,
        is_active: formData.is_active
      };

      // Add session count based on type
      if (formData.type === 'pack') {
        serviceData.session_count = parseInt(formData.total_sessions) || 0;
      } else {
        serviceData.session_count = parseInt(formData.monthly_sessions) || 0;
      }

      if (mode === 'create') {
        await adminAPI.createServiceType(serviceData);
        toast.success('Service créé avec succès');
      } else if (mode === 'edit') {
        await adminAPI.updateServiceType(service.id, serviceData);
        toast.success('Service modifié avec succès');
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error(error.message || 'Erreur lors de la sauvegarde');
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (!isOpen) return null;

  const isReadOnly = mode === 'view';
  const title = mode === 'create' ? 'Nouveau Service' : mode === 'edit' ? 'Modifier Service' : 'Détails Service';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            {(mode === 'view' || mode === 'edit') && service?.id && (
              <button
                onClick={() => navigate(`/services/${service.id}/clients`)}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100"
              >
                <UsersIcon className="h-4 w-4 mr-1" />
                Voir les clients ({clientServices.length})
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {errors.general}
            </div>
          )}

          <div className={`grid grid-cols-1  ${mode === 'create'? 'lg:grid-cols-1': 'lg:grid-cols-2'} gap-8`}>
            {/* Left Column - Service Information */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CreditCardIcon className="h-5 w-5 mr-2" />
                    Informations de base
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom du service *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={isReadOnly}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type de service *
                      </label>
                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                        required
                        disabled={isReadOnly}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                      >
                        <option value="pack">Pack (nombre fixe de séances)</option>
                        <option value="abonnement">Abonnement (séances mensuelles)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        disabled={isReadOnly}
                        placeholder="Décrivez ce service..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                      />
                    </div>
                  </div>
                </div>

                {/* Sessions Configuration */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <ClockIcon className="h-5 w-5 mr-2" />
                    Configuration des séances
                  </h3>
                  <div className="space-y-4">
                    {formData.type === 'pack' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nombre total de séances *
                        </label>
                        <input
                          type="number"
                          name="total_sessions"
                          value={formData.total_sessions}
                          onChange={handleChange}
                          min="1"
                          required
                          disabled={isReadOnly}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Séances par mois *
                        </label>
                        <input
                          type="number"
                          name="monthly_sessions"
                          value={formData.monthly_sessions}
                          onChange={handleChange}
                          min="1"
                          required
                          disabled={isReadOnly}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Durée de validité (jours)
                      </label>
                      <input
                        type="number"
                        name="duration_days"
                        value={formData.duration_days}
                        onChange={handleChange}
                        min="1"
                        disabled={isReadOnly}
                        placeholder={formData.type === 'pack' ? 'Ex: 60 jours' : 'Ex: 30 jours par mois'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                      />
                    </div>
                  </div>
                </div>

                {/* Pricing */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                    Tarification
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prix (MAD)
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                    />
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Service actif</span>
                  </label>
                </div>

                {/* Actions */}
                {!isReadOnly && (
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      {loading ? 'Sauvegarde...' : (mode === 'create' ? 'Créer' : 'Modifier')}
                    </button>
                  </div>
                )}
              </form>
            </div>

            {/* Right Column - Clients Using This Service (only for view/edit modes) */}
            {(mode === 'view' || mode === 'edit') && (
              <div>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <DocumentTextIcon className="h-5 w-5 mr-2" />
                      Clients utilisant ce service
                    </h3>
                    {loadingClients ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                      </div>
                    ) : clientServices.length > 0 ? (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {clientServices.map((clientService) => (
                          <div key={clientService.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-gray-900">
                                {clientService.client?.full_name || 'Client'}
                              </h4>
                              {(() => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const endDate = new Date(clientService.end_date);
                                endDate.setHours(0, 0, 0, 0);

                                const isExpired = endDate < today;
                                const hasNoSessions = formData.type === 'pack' ?
                                  clientService.used_sessions >= clientService.total_sessions :
                                  clientService.current_month_used >= (service?.session_count || 0);

                                const isTrulyActive = clientService.is_truly_active !== undefined ? clientService.is_truly_active :
                                  (clientService.status === 'active' && !isExpired && !hasNoSessions);

                                let statusText = 'Actif';
                                let statusClass = 'bg-green-100 text-green-800';

                                if (!isTrulyActive) {
                                  if (isExpired) {
                                    statusText = 'Expiré';
                                    statusClass = 'bg-red-100 text-red-800';
                                  } else if (hasNoSessions) {
                                    statusText = 'Épuisé';
                                    statusClass = 'bg-orange-100 text-orange-800';
                                  } else if (clientService.status !== 'active') {
                                    statusText = 'Inactif';
                                    statusClass = 'bg-gray-100 text-gray-800';
                                  }
                                }

                                return (
                                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusClass}`}>
                                    {statusText}
                                  </span>
                                );
                              })()}
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <div>Email: {clientService.client?.email || 'N/A'}</div>
                              {formData.type === 'pack' ? (
                                <>
                                  <div>Sessions: {clientService.used_sessions || 0} / {clientService.total_sessions || 0}</div>
                                  <div>Période: {clientService.start_date} - {clientService.end_date}</div>
                                </>
                              ) : (
                                <>
                                  <div>Sessions ce mois: {clientService.current_month_used || 0} / {service?.session_count || 0}</div>
                                  <div>Abonnement: {clientService.start_date} - {clientService.end_date}</div>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <DocumentTextIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                        <p>Aucun client n'utilise ce service</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceModal;
